import { Button, Col, Divider, Row } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import BadgeCharityContext from "../../../context/badge_charity/context";
import BadgeCharityContextProvider from "../../../context/badge_charity/provider";
import { MediumFor } from "../../../models/medium/enum";
import { IUpdateBadgeCharity } from "../../../models/badge_charity/request";
import { languages } from "../../../utils/helpers/constants";
import { getTranslations } from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import FieldBuilder from "../../form-components/field-builder";
import ImageUploader from "../../form-components/image-uploader";
import MainForm from "../../form-components/main-form";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import { useState } from "react";
import OldImage from "../../form-components/old-image";
import { FormProvider, useForm } from "react-hook-form";
import styles from "./style.module.scss";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";

const Form = () => {
  const { t } = useTranslation();
  const { details, actions, loading } = useContext(BadgeCharityContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const { direction } = useContext(AppContext);

  const methods = useForm<any>({
    defaultValues: {
      name: getTranslations(details?.translations?.name),
      note: getTranslations(details?.translations?.note),
      imageId: details?.image?.id ?? undefined,
    } as IUpdateBadgeCharity,
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    reset({
      name: getTranslations(details?.translations?.name),
      note: getTranslations(details?.translations?.note),
      imageId: details?.image?.id ?? undefined,
    });
  }, [details]);

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const onSubmit = async (data: any) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        details
          ? await actions.updateBadgeCharity(details?.id, data)
          : await actions.createBadgeCharity(data);
        navigate(-1);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_badge_charity") : t("add_badge_charity")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[16, 8]}>
                {languages?.map((lang, index) => (
                  <Col span={12} key={index}>
                    <FieldBuilder
                      name={`name[${lang?.code}]`}
                      label={`${t("name")} (${lang?.name})`}
                      input={{ type: "text" }}
                      rules={{ required: true }}
                    />
                  </Col>
                ))}
                {languages?.map((lang, index) => (
                  <Col span={12} key={index}>
                    <FieldBuilder
                      name={`note[${lang?.code}]`}
                      label={`${t("note")} (${lang?.name})`}
                      input={{ type: "text-area" }}
                      rules={{ required: false }}
                    />
                  </Col>
                ))}
                <Col span={24}>
                  <FormItem label={t("image")} required>
                    <Controller
                      name="imageId"
                      rules={{
                        required: {
                          value: true,
                          message: `${t("field_is_required_message")}`,
                        },
                      }}
                      render={({ field: { ref, ...field } }) => (
                        <ImageUploader
                          {...field}
                          type="DRAGGER"
                          onChange={(response) => {
                            field.onChange(response?.id);
                          }}
                          imageFor={MediumFor.UserProfile}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
                {details?.image && <OldImage src={details?.image?.url} />}
              </Row>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button onClick={() => navigate(-1)}>
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const BadgeCharityForm = () => {
  return (
    <BadgeCharityContextProvider>
      <Form />
    </BadgeCharityContextProvider>
  );
};

export default BadgeCharityForm;